import { IServerSideGetRowsParams } from '@ag-grid-enterprise/all-modules';
import { Box, makeStyles } from '@material-ui/core';
import { useParams } from '@reach/router';
import { RowNode } from 'ag-grid-community';
import React, {
  Dispatch,
  /* to be used with ag-grid v25.0.0 useEffect, */ useMemo,
  useState,
} from 'react';
import ReactGA from 'react-ga';
import { useTranslation } from 'react-i18next';

import RawReportColumnSelectionModal from '../../../../components/Modals/rawReportColumnSelectionModal';
import TableGrid from '../../../../components/TableGrid';
import TablePagination from '../../../../components/TableGrid/Pagination';
import {
  useTableGridApiDispatch,
  useTableGridApiState,
} from '../../../../components/TableGrid/TableGridApiContext/TableGridApiContext';
import { TableGridApiAction } from '../../../../components/TableGrid/TableGridApiContext/TableGridApiContext.types';
import DataGridToolbar from '../../../../components/TableGrid/TableGridToolbar';
import ActionBox from '../../../../components/TableGrid/TableGridToolbar/ActionBox/ActionBox';
import { ActionLink } from '../../../../components/TableGrid/TableGridToolbar/ActionBox/ActionBox.type';
import CountBox from '../../../../components/TableGrid/TableGridToolbar/CountBox/CountBox';
import { useAuth } from '../../../../contexts/AuthContext/AuthContext';
import { api } from '../../../../core/api';
import { fetchBenchmarkDetails, updateRefinementSearchId } from '../../../../core/api/benchmark';
import { companySearch } from '../../../../core/api/companySearch';
import { SearchReportTypes } from '../../../../core/api/search';
import { CONSTANTS } from '../../../../core/config/constants';
import { isTrialAccess } from '../../../../core/utils/access.utils';
import { CompanySearchRequest, SearchQuerySaveRequest } from '../../../../generated/model/models';
import { useAppGoogleReCaptcha } from '../../../../hooks/useAppGoogleReCaptcha';
import {
  useBenchmarkDispatch,
  useBenchmarkState,
} from '../../../Benchmark/Context/BenchmarkContext';
import { BenchmarkContextActionTypes } from '../../../Benchmark/Context/BenchmarkContext.types';
import {
  useCompaniesDispatch,
  useCompaniesState,
} from '../../shared/CompanyContext/CompanyContext';
import {
  CompanyContextActions,
  CompanyContextActionTypes,
} from '../../shared/CompanyContext/CompanyContext.types';
import { useErrorsDispatch } from '../../shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../shared/ErrorContext/ErrorContext.types';
import {
  useFilterDispatch,
  useFilterState,
} from '../../shared/FinancialDataContext/FinancialDataContext';
import {
  AgGridSortModel,
  FinancialDataContextActionTypes,
  RequestParams,
} from '../../shared/FinancialDataContext/FinancialDataContext.types';
import { useSearchState } from '../../shared/SearchContext/SearchContext';
import SearchListColumns, {
  searchListColumnPresets,
  searchListDefaultColumns,
} from './SearchListColumns';
import SearchSummary from './TableGrid/TableGridToolbar/SearchSummary/SearchSummary';
import SendBenchmark from './TableGrid/TableGridToolbar/SendBenchmark/SendBenchmark';

interface OwnProps {
  benchmarkRefinement: boolean;
}

const getActionItems = (
  queryRequest: SearchQuerySaveRequest,
  selectedRows: number,
  totalElements: number,
  downloadIsForbidden: boolean,
  params?: RequestParams,
): ActionLink[] => [
  {
    title: 'companies:downloadReportRaw',
    snackbarReportText: 'companies:snackbarReportRawAndMarketingText',
    onClick: async () => {
      ReactGA.event({ category: 'SEARCH', action: 'LIST_REPORT' });
      await api.search.getReport(SearchReportTypes.RAW, queryRequest, params);
    },
    count: selectedRows > 0 ? selectedRows : totalElements,
    isNotActive: false,
    isHidden: true,
  },
  {
    title: 'companies:downloadReportRawSelected',
    snackbarReportText: 'companies:snackbarReportRawAndMarketingText',
    count: selectedRows > 0 ? selectedRows : totalElements,
    isNotActive: false,
    isHidden: downloadIsForbidden,
    customDialog: (
      open: boolean,
      handleClose: () => void,
      handleConfirmOk: (onClick: () => Promise<void>) => void,
    ) => (
      <RawReportColumnSelectionModal
        open={open}
        handleClose={handleClose}
        handleConfirmOk={handleConfirmOk}
        queryRequest={queryRequest}
      />
    ),
  },
  {
    title: 'companies:downloadReportMarketing',
    snackbarReportText: 'companies:snackbarReportRawAndMarketingText',
    onClick: async () => {
      ReactGA.event({ category: 'SEARCH', action: 'STRUCTURED_REPORT' });
      await api.search.getReport(SearchReportTypes.MARKETING, queryRequest, params);
    },
    count: selectedRows > 0 ? selectedRows : totalElements,
    isNotActive: false,
    isHidden: downloadIsForbidden,
  },
  {
    title: 'companies:downloadReportBoth',
    snackbarReportText: 'companies:snackbarReportBothText',
    onClick: async () => {
      ReactGA.event({ category: 'SEARCH', action: 'BOTH_REPORTS' });
      await api.search.getBothReports(queryRequest, params);
    },
    count: selectedRows > 0 ? selectedRows : totalElements,
    isNotActive: false,
    isHidden: true,
  },
];

const SearchList: React.FC<OwnProps> = ({ benchmarkRefinement }) => {
  const {
    companies,
    pagination,
    selectedCompanies,
    filterButtonClickState,
    dataset,
  } = useCompaniesState();
  const { t } = useTranslation();
  const classes = useStyles();
  const { requestFiltersData, historySearch, sortModel } = useFilterState();
  const [sortingAllowed, setSortingAllowed] = useState<boolean>(false);
  const { benchmarkId } = useParams();
  const columns = SearchListColumns(t, sortingAllowed, benchmarkId);
  const dispatchCompanies = useCompaniesDispatch();
  const { setSearchRequest } = useSearchState();
  const dispatchErrors = useErrorsDispatch();
  const { tableGridApi, selectedRows } = useTableGridApiState();
  const dispatchTable = useTableGridApiDispatch();
  const { activeBenchmark } = useBenchmarkState();
  const benchmarkDispatch = useBenchmarkDispatch();
  const [totalCount, setTotalCount] = useState<number>(0);
  const { executeRecaptcha } = useAppGoogleReCaptcha();
  const { user } = useAuth();
  const isDownloadForbidden = isTrialAccess(user, dataset);
  const dispatchFilter = useFilterDispatch();

  const queryRequestData: SearchQuerySaveRequest = {
    searchRequest: { filters: requestFiltersData, historySearch },
    ids: selectedRows,
  };
  /* used in ag-grid v25.0.0
  useEffect(() => {
    if (filterButtonClickState) {
      tableGridApi?.refreshServerSideStore({});
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestFiltersData, filterButtonClickState]);
*/
  const params: RequestParams = {};

  if (sortModel?.length) {
    params.sort = sortModel.map((sortProperty) => `${sortProperty.colId},${sortProperty.sort}`);
  }

  const actionItems = useMemo(
    () =>
      getActionItems(
        queryRequestData,
        selectedRows.length,
        pagination.totalElements,
        isDownloadForbidden,
        params,
      ),
    [queryRequestData, selectedRows, pagination.totalElements, isDownloadForbidden, params],
  );

  return (
    <TableGrid
      columnsStorageId="financial-data"
      translationKey="companies:resultTable"
      tableToolbar={
        <DataGridToolbar>
          <CountBox
            boxTitle={'app:datagrid:results'}
            totalCount={totalCount}
            count={pagination.totalElements}
            selectedCount={selectedRows.length}
          />
          <Box className={classes.rightActionBox}>
            <SearchSummary />
            <ActionBox buttonTitle={t('app:download')} actionItems={actionItems} />
            <SendBenchmark benchmarkRefinement={benchmarkRefinement} totalCount={totalCount} />
          </Box>
        </DataGridToolbar>
      }
      columns={columns}
      getDatasource={getDatasource(executeRecaptcha)}
      rowSelection={true}
      rowSelectionIdGetter={rowSelectionIdGetter}
      initialSelectedRows={selectedCompanies}
      onSelectionChanged={onSelectionChanged(dispatchCompanies)}
      suppressTableContextProvider={true}
      isSideBarCollapsedByDefault={false}
      initialSortModel={sortModel}
      systemDefaultFields={searchListDefaultColumns()}
      systemDefaultPresets={searchListColumnPresets()}
      originalProps={{
        context: {
          requestFiltersData,
          pagination,
          historySearch,
          companies,
          dispatchCompanies,
          setSearchRequest,
          filterButtonClickState,
          dispatchTable,
          setTotalCount,
          setSortingAllowed,
          dispatchErrors,
          activeBenchmark,
          benchmarkRefinement,
          benchmarkDispatch,
          dispatchFilter,
        },
        headerHeight: CONSTANTS.TABLE_HEADER - 1,
        rowModelType: 'serverSide',
        pagination: true,
        paginationPageSize: pagination.size,
        cacheBlockSize: pagination.size,
        maxBlocksInCache: 1,
        suppressPaginationPanel: true,
        rowSelection: 'multiple',
        rowMultiSelectWithClick: true,
        suppressRowClickSelection: true,
        defaultColDef: defaultColumnProp,
      }}
    >
      {pagination.totalElements > 0 ? (
        <TablePagination
          gridApi={tableGridApi}
          pagination={pagination}
          onChange={(value: number) => {
            dispatchCompanies({
              type: CompanyContextActionTypes.SetPagination,
              payload: { ...pagination, page: value },
            });
          }}
        />
      ) : null}
    </TableGrid>
  );
};

export const onSelectionChanged = (dispatchCompanies: Dispatch<CompanyContextActions>) => (
  ids: string[],
) => {
  dispatchCompanies({
    type: CompanyContextActionTypes.SelectCompanies,
    payload: ids,
  });
};

export const rowSelectionIdGetter = (node: RowNode) => {
  return node.data?.companyInfo?.id ?? '';
};

export function getDatasource(executeRecaptcha: any) {
  return {
    getRows: async (params: IServerSideGetRowsParams) => {
      // quick fix to get provided context params
      const model: any = params.api.getModel();
      const sortModel: AgGridSortModel = params.api.getSortModel();
      const {
        requestFiltersData,
        pagination,
        historySearch,
        dispatchCompanies,
        setSearchRequest,
        dispatchErrors,
        filterButtonClickState,
        dispatchTable,
        setTotalCount,
        setSortingAllowed,
        activeBenchmark,
        benchmarkRefinement,
        benchmarkDispatch,
        dispatchFilter,
      } = model.getContext().getBean('gridOptions').context;
      let totalPages = 0;
      let totalElements = 0;
      let totalCount = 0;
      let sortingAllowed = false;

      const requestData: CompanySearchRequest = { filters: requestFiltersData, historySearch };

      const requestParams: RequestParams = {
        page: undefined,
        size: undefined,
      };

      if (sortModel?.length) {
        requestParams.sort = sortModel.map(
          (sortProperty) => `${sortProperty.colId},${sortProperty.sort}`,
        );

        dispatchFilter?.({
          type: FinancialDataContextActionTypes.SetSortModel,
          payload: sortModel,
        });
      } else {
        dispatchFilter?.({
          type: FinancialDataContextActionTypes.ResetSortModel,
        });
      }

      requestParams.page = pagination.page > 0 ? pagination.page - 1 : 0;
      requestParams.size = pagination.size;

      // reset selected rows state after filter button is clicked
      if (filterButtonClickState) {
        dispatchTable({ type: TableGridApiAction.ResetSelectedRows });
      }

      if (!Object.keys(requestFiltersData).length) {
        params.api.showNoRowsOverlay();
        return params.successCallback([], 0);
      }

      try {
        const recaptchaToken = await executeRecaptcha('search');
        if (setSearchRequest) {
          setSearchRequest(requestData);
        }
        const response: any = await companySearch(requestData, recaptchaToken, requestParams);
        dispatchCompanies({
          type: CompanyContextActionTypes.SetIsLoadingState,
          payload: true,
        });

        if (response) {
          if (activeBenchmark?.id && benchmarkRefinement) {
            await updateRefinementSearchId(activeBenchmark.id, {
              refinementSearchId: response?.data?.searchId,
            });
            const updatedBenchmark = await fetchBenchmarkDetails(activeBenchmark.id);
            benchmarkDispatch({
              type: BenchmarkContextActionTypes.SetActiveBenchmark,
              payload: updatedBenchmark,
            });
          }
          // supply rows for requested block to grid
          const result = response?.data?.searchResult?.content ?? [];
          if (result.length <= 0) {
            // if response has no rows display noRowsOverlay
            params.api.showNoRowsOverlay();
            params.successCallback([], 0);
          } else {
            totalElements = response?.data?.searchResult?.totalElements;
            totalPages = response?.data?.searchResult?.totalPages;
            totalCount = response?.data?.totalCount;
            params.successCallback(result, totalElements);
            params.api.hideOverlay();
            sortingAllowed =
              !(window as any)?.env?.DISABLE_SEARCH_RESULTS_SORTING &&
              (window as any)?.env?.MINIMUM_SORTING_DB_VERSION <= response?.data?.dbVersion;
            dispatchCompanies({
              type: CompanyContextActionTypes.SetCompanies,
              payload: {
                companies: result,
                searchId: response?.data?.searchId,
                dataset: response?.data?.dataset,
                dbVersion: response?.data?.dbVersion,
              },
            });
            // TODO: refactor to use one request to BE instead of two
            if (params.request.startRow !== requestParams.page * pagination.size) {
              params.api.paginationGoToPage(requestParams.page);
            }
          }
          if (setTotalCount) {
            setTotalCount(totalCount);
          }
          if (setSortingAllowed) {
            setSortingAllowed(sortingAllowed);
          }
        } else {
          // inform grid request failed
          params.failCallback();
        }
      } catch (error) {
        dispatchErrors({
          type: ErrorContextActionTypes.SetError,
          payload: { error },
        });
        // failed request
        params.api.showNoRowsOverlay();
        params.successCallback([], 0);
      } finally {
        dispatchCompanies({
          type: CompanyContextActionTypes.SetPagination,
          payload: {
            ...pagination,
            totalPages,
            totalElements,
          },
        });
        dispatchCompanies({
          type: CompanyContextActionTypes.SetFilterButtonClickState,
          payload: false,
        });
        dispatchCompanies({
          type: CompanyContextActionTypes.SetIsLoadingState,
          payload: false,
        });
      }
    },
  };
}

const defaultColumnProp = {
  resizable: true,
  flex: 1,
  minWidth: 100,
  menuTabs: [],
};

const useStyles = makeStyles(() => ({
  rightActionBox: {
    marginLeft: 'auto',
    position: 'relative',
    display: 'flex',
  },
}));

export default SearchList;
